import { faTrophy } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage } from 'react-intl'
import { styled } from 'styled-components'

import {
  isAuctionListing,
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { useFdActiveGroup } from '@/buyer-portal/components/features/listing/hooks/useFdActiveGroup'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'

import { formatCountdown, useFirstDibsCountdown } from './useFirstDibsCountdown'

// Matches the First Dibs LDP design (no design-system token for #F0F6FF).
const BANNER_BACKGROUND = '#F0F6FF'

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.25rem 0.625rem;
  margin: 1rem 0;
  padding: 0.625rem 1rem;
  border-radius: 0.25rem;
  background: ${BANNER_BACKGROUND};
  color: ${Colors.Semantic.BStock.default.lightBackground};
`

const TrophyIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
`

const Label = styled.span`
  ${Typography.Overline2}
`

const Countdown = styled.span`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25rem;
  white-space: nowrap;
`

/**
 * Full-width LDP banner counting down until a First Dibs listing opens to the
 * general marketplace. Shown only while First Dibs applies (same rule as the
 * bid card: the buyer's window is open and the auction is still Scheduled).
 */
export const FirstDibsOpensBanner = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) =>
  isAuctionListing(listing) ? (
    <AuctionFirstDibsOpensBanner listing={listing} />
  ) : (
    <OfferingFirstDibsOpensBanner listing={listing} />
  )

const OfferingFirstDibsOpensBanner = ({
  listing,
}: {
  listing: OrchestratedOfferingListingDetails
}) => {
  const { fdWindowActive } = useFdActiveGroup(listing.firstDibs)
  const remaining = useFirstDibsCountdown(listing.firstDibs)

  if (!fdWindowActive || remaining <= 0) return null

  return (
    <Banner data-testid="first-dibs-opens-banner">
      <TrophyIcon icon={faTrophy} />
      <Label>
        <FormattedMessage id="Listing.firstDibsBanner.opensToEveryoneIn" />
      </Label>
      <Countdown suppressHydrationWarning>
        {formatCountdown(remaining)}
      </Countdown>
    </Banner>
  )
}

const AuctionFirstDibsOpensBanner = ({
  listing,
}: {
  listing: OrchestratedAuctionListingDetails
}) => {
  const { fdActive } = useFirstDibsPurchase(listing)
  const remaining = useFirstDibsCountdown(listing.firstDibs)

  if (!fdActive || remaining <= 0) return null

  return (
    <Banner data-testid="first-dibs-opens-banner">
      <TrophyIcon icon={faTrophy} />
      <Label>
        <FormattedMessage id="Listing.firstDibsBanner.opensToEveryoneIn" />
      </Label>
      <Countdown suppressHydrationWarning>
        {formatCountdown(remaining)}
      </Countdown>
    </Banner>
  )
}
